// src/Pages/Assets/DepositRecordsPage.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDepositIntents,
  fetchDepositHistory,
} from "../../store/Slices/depositSlice";
import {
  Box,
  Container,
  Paper,
  Typography,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/History";
import AppTopBar from "../../Components/AppTopBar";

function Row({ k, v, copy = false, shrink = false }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        variant="body2"
        sx={{ minWidth: 120, color: "text.secondary" }}
      >
        {k}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          ...(shrink
            ? {
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {}),
        }}
      >
        {v}
      </Typography>
    </Box>
  );
}

export default function DepositRecordsPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const brand =
    theme.palette?.brand?.main || theme.palette?.primary?.main || "#f0b90b";
  const [tab, setTab] = useState(0);

  const {
    intents,
    intentsLoading,
    history,
    historyLoading,
    error,
  } = useSelector((s) => s.deposit);

  useEffect(() => {
    dispatch(fetchDepositIntents());
    dispatch(fetchDepositHistory());
  }, [dispatch]);

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: "100vh" }}>
      <AppTopBar title="Deposit Records" showBack />

      <Container maxWidth="md" sx={{ pt: 10, pb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: 2,
            border: `1px solid ${theme.palette.text.secondary}22`,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: brand } }}
          >
            <Tab
              icon={<ListAltIcon fontSize="small" />}
              iconPosition="start"
              label="Intents"
            />
            <Tab
              icon={<HistoryIcon fontSize="small" />}
              iconPosition="start"
              label="History"
            />
          </Tabs>
          <Divider />

          <Box sx={{ p: 2.5 }}>
            {tab === 0 ? (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1.5, fontWeight: 700 }}
                >
                  Recent Deposit Intents
                </Typography>
                {intentsLoading ? (
                  <LinearProgress />
                ) : intents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No intents yet.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.2,
                      maxHeight: 420,
                      overflow: "auto",
                    }}
                  >
                    {intents.slice(0, 50).map((it) => (
                      <Paper
                        key={it._id || it.intent_id}
                        variant="outlined"
                        sx={{ p: 1.2, borderRadius: 2 }}
                      >
                        <Row k="Asset" v={it.asset_symbol} />
                        <Row k="Network" v={it.network_name} />
                        <Row k="Amount" v={it.expected_amount} />
                        <Row k="Address" v={it.deposit_address} shrink />
                        {it.memo_tag && <Row k="Tag/Memo" v={it.memo_tag} />}
                        <Row k="Status" v={it.status} />
                      </Paper>
                    ))}
                  </Box>
                )}
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => dispatch(fetchDepositIntents())}
                  >
                    Refresh
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1.5, fontWeight: 700 }}
                >
                  Deposit History (last 100)
                </Typography>
                {historyLoading ? (
                  <LinearProgress />
                ) : history.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No deposits yet.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 460, overflow: "auto" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Asset</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.asset}</TableCell>
                            <TableCell>{h.amount}</TableCell>
                            <TableCell>
                              <Chip
                                label={h.status}
                                size="small"
                                color={
                                  h.status === "Completed"
                                    ? "success"
                                    : h.status === "Pending"
                                    ? "warning"
                                    : "default"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(h.created_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => dispatch(fetchDepositHistory())}
                  >
                    Refresh
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        {(intentsLoading || historyLoading) && (
          <LinearProgress sx={{ mt: 2 }} />
        )}
        {error && (
          <Typography sx={{ mt: 2, color: "error.main" }}>
            {String(error)}
          </Typography>
        )}
      </Container>
    </Box>
  );
}
